import { useEffect, useState } from 'react';
import { FiMoon, FiX } from 'react-icons/fi';
import { Button } from '@/components';

type CalmFilterModalProps = {
  open: boolean;
  onClose: () => void;
  enabled: boolean;
  onEnabledChange: (value: boolean) => void;
  quietStartMinutes: number;
  quietEndMinutes: number;
  onApply: (values: {
    enabled: boolean;
    quietStartMinutes: number;
    quietEndMinutes: number;
  }) => void;
  isApplying?: boolean;
};

function minutesToTimeValue(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function timeValueToMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

const DESCRIPTION_ITEMS = [
  'No chat push notifications during quiet time',
  'Messages still show in Harbor immediately',
  'Other notifications remain the same',
] as const;

export function CalmFilterModal({
  open,
  onClose,
  enabled,
  onEnabledChange,
  quietStartMinutes,
  quietEndMinutes,
  onApply,
  isApplying = false,
}: CalmFilterModalProps) {
  const [startTime, setStartTime] = useState(minutesToTimeValue(quietStartMinutes));
  const [endTime, setEndTime] = useState(minutesToTimeValue(quietEndMinutes));

  useEffect(() => {
    if (open) {
      setStartTime(minutesToTimeValue(quietStartMinutes));
      setEndTime(minutesToTimeValue(quietEndMinutes));
    }
  }, [open, quietStartMinutes, quietEndMinutes]);

  if (!open) return null;

  const handleApply = () => {
    onApply({
      enabled,
      quietStartMinutes: timeValueToMinutes(startTime),
      quietEndMinutes: timeValueToMinutes(endTime),
    });
  };

  return (
    <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto'>
        <div className='p-6'>
          <div className='flex items-start justify-between mb-4'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center'>
                <FiMoon className='w-5 h-5 text-primary' />
              </div>
              <div>
                <h2 className='text-xl font-bold text-gray-900'>Calm filter</h2>
                <p className='text-sm text-gray-500'>Quiet hours for chat notifications</p>
              </div>
            </div>
            <button
              type='button'
              onClick={onClose}
              className='p-2 hover:bg-gray-100 rounded-full'
              aria-label='Close'
            >
              <FiX className='w-5 h-5 text-gray-500' />
            </button>
          </div>

          <ul className='space-y-2 mb-5'>
            {DESCRIPTION_ITEMS.map((text) => (
              <li key={text} className='text-sm text-gray-600 flex gap-2'>
                <span className='text-primary'>•</span>
                {text}
              </li>
            ))}
          </ul>

          <div className='flex items-center justify-between py-3 border-t border-gray-100'>
            <span className='font-semibold text-gray-900'>Enable calm filter</span>
            <label className='relative inline-flex items-center cursor-pointer'>
              <input
                type='checkbox'
                checked={enabled}
                onChange={(e) => onEnabledChange(e.target.checked)}
                className='sr-only peer'
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
            </label>
          </div>

          {enabled && (
            <div className='space-y-4 mt-2'>
              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-1'>
                  Quiet starts
                </label>
                <input
                  type='time'
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className='w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30'
                />
              </div>
              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-1'>
                  Quiet ends
                </label>
                <input
                  type='time'
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className='w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30'
                />
              </div>
            </div>
          )}

          <div className='flex gap-3 mt-6'>
            <Button variant='secondary' fullWidth onClick={onClose} disabled={isApplying}>
              Cancel
            </Button>
            <Button fullWidth onClick={handleApply} loading={isApplying} disabled={isApplying}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
